import { useEffect, useRef, useState, type MouseEvent } from 'react';
import { flushSync } from 'react-dom';
import { useLoaderData } from 'react-router';
import { useTracker } from '@archipro-website/tracker';

import { RoccoLink } from '@rocco/components/link';
import { Spinner } from '@rocco/components/spinner';
import { cn } from '@rocco/utils/cn';
import { useAuth } from '~/modules/root/context';
import { useAuthGating } from '~/modules/root/hook/use-auth-gating';
import type { loader } from '~/routes/_app.project.$urlSegment._index';
import {
    CONTENT_CAP_PADDED,
    useFollowCursorBubble,
} from '~/ui-rocco/pages/detail-shared';

/**
 * P6 — "Featured Publication for this Project" (Figma 2354:59890). When a
 * featured article is set in Business Manager, its title sits as a giant
 * editorial statement on the dark background. Hovering it swaps the cursor for a
 * white "Read Article" bubble that eases after the pointer (the dark-mode
 * counterpart of the product page's tagged-projects statement). A click swaps
 * that label for a spinner and pins the bubble until the article route
 * resolves, so a slow load is not a dead click. Hidden when no featured
 * article is selected.
 */
export const ProjectStorySection = () => {
    const { project } = useLoaderData<typeof loader>();

    // FeaturedArticle is set in BM. The generated GraphQL type lags until
    // `pnpm gql` runs, so read it through a narrow cast for now.
    const featuredArticle = (
        project as typeof project & {
            FeaturedArticle?: {
                ID: number;
                Title: string;
                Link: string;
            } | null;
        }
    ).FeaturedArticle;

    const eyebrowRef = useRef<HTMLSpanElement | null>(null);
    const titleRef = useRef<HTMLSpanElement | null>(null);
    const { enabled: articleGated } = useAuthGating('article-gating');
    const { showAuthModal } = useAuth();
    const tracker = useTracker();
    // The bubble is the cursor. A primary click swaps its label for a spinner
    // and pins it until the document navigates away, so a slow load is not a
    // dead click. Modified clicks (new tab) leave the label alone when the
    // article is public. Article-gating guests never reach that route — the
    // loader 401s — so every click (including modified) opens the keep-reading
    // sheet. Middle-click is cancelled on mousedown because auxclick cannot
    // stop the browser opening a tab.
    const [opening, setOpening] = useState(false);
    const { bubbleRef, bubbleVisible, sectionHandlers } = useFollowCursorBubble(
        {
            triggers: [eyebrowRef, titleRef],
            held: opening,
        },
    );

    // Back from the article restores this document from bfcache with
    // `opening` still true, so the spinner would sit on the title forever.
    useEffect(() => {
        const release = () => setOpening(false);
        window.addEventListener('pageshow', release);
        return () => window.removeEventListener('pageshow', release);
    }, []);

    if (!featuredArticle) return null;

    const openKeepReadingSheet = () => {
        showAuthModal({
            authSource: 'articleGate',
            skipPostSignUp: true,
            redirect: featuredArticle.Link,
        });
    };

    // Middle-click opens the new tab from mousedown; auxclick cannot cancel it.
    const onFeaturedArticleMouseDown = (
        event: MouseEvent<HTMLAnchorElement>,
    ) => {
        if (!articleGated || event.button !== 1) return;
        event.preventDefault();
        openKeepReadingSheet();
    };

    // Every way into the article counts, the keep-reading sheet included.
    const trackFeaturedArticleClick = () => {
        void tracker.log('ProjectFeaturedArticleClick', {
            url: new URL(window.location.href),
            data: {
                LibraryItemID: featuredArticle.ID,
                ExtraData: String(featuredArticle.ID),
            },
        });
    };

    const onFeaturedArticleClick = (event: MouseEvent<HTMLAnchorElement>) => {
        trackFeaturedArticleClick();
        // Gate before the modified-click bail-out so cmd/ctrl/shift/alt-click
        // get the keep-reading sheet instead of a URL the loader will 401.
        if (articleGated) {
            event.preventDefault();
            openKeepReadingSheet();
            return;
        }
        if (
            event.metaKey ||
            event.ctrlKey ||
            event.shiftKey ||
            event.altKey ||
            event.button !== 0
        ) {
            return;
        }
        event.preventDefault();
        // Commit the spinner before leaving. assign() in the same tick can
        // unload before React paints, which is the flash-then-gone spinner.
        // A Remix client transition aborts and leaves the project page; a
        // document load reaches the article, and `opening` stays true until
        // that unload.
        flushSync(() => setOpening(true));
        window.location.assign(featuredArticle.Link);
    };

    const onFeaturedArticleAuxClick = (
        event: MouseEvent<HTMLAnchorElement>,
    ) => {
        if (event.button !== 1) return;
        trackFeaturedArticleClick();
        if (!articleGated) return;
        event.preventDefault();
        openKeepReadingSheet();
    };

    return (
        <section
            {...sectionHandlers}
            className={cn(
                'relative w-full bg-transparent text-white max-md:px-6 md:px-[3rem]',
                // Mirror the product's tagged-projects statement: enlarge the
                // cursor/hover target into the neighbouring spacers without
                // shifting content (each padding cancelled by an equal negative
                // margin), capped within the 4rem/3.5rem gaps to the credits row
                // above and the accordion below so it never overshoots them.
                'md:-mt-[4rem] md:-mb-[3rem] md:pt-[4rem] md:pb-[3rem]',
                // Custom "Read Article" cursor replaces the pointer over the
                // trigger on desktop; mobile keeps the default tap target.
                'md:cursor-none md:[&_a]:cursor-none',
            )}
        >
            <RoccoLink
                to={featuredArticle.Link}
                aria-busy={opening || undefined}
                onMouseDown={onFeaturedArticleMouseDown}
                onClick={onFeaturedArticleClick}
                onAuxClick={onFeaturedArticleAuxClick}
                className={cn(
                    CONTENT_CAP_PADDED,
                    // Above the magic island so a click on the title reaches
                    // this link, not the pin/profile pill. The bubble sits
                    // higher still (and is pointer-events-none) so the
                    // circle covers the letter counters instead of showing
                    // through them.
                    'relative z-[100] block text-left',
                )}
            >
                <span
                    ref={eyebrowRef}
                    className="block w-fit font-sans leading-[1.22] font-medium tracking-[-0.05em] text-[#9a9a9a] max-md:text-sm md:text-[0.9375rem]"
                >
                    Featured Publication for this Project
                </span>
                {/* Giant editorial title (Figma TWK Ghost, 77.8px / white). No
                    line-clamp: its overflow:hidden plus the tight leading-[0.94]
                    clips the cap-tops and descenders. max-width + text-balance
                    keep it to ~two balanced lines, matching the product statement.
                    On a phone a tap fades the words and parks a spinner in the
                    middle of them — no extra button. */}
                <span className="relative mt-[1.7rem] block w-fit max-w-[94.16rem] max-md:mt-3">
                    <span
                        ref={titleRef}
                        className={cn(
                            'block font-twk-ghost font-normal text-balance text-white max-md:text-[2.25rem] max-md:leading-[1.05] max-md:tracking-[-0.02em] md:text-[4.86rem] md:leading-[0.94] md:tracking-[-0.096rem]',
                            'max-md:transition-opacity max-md:duration-300',
                            opening && 'max-md:opacity-30',
                        )}
                    >
                        {featuredArticle.Title}
                    </span>
                    {opening ? (
                        <span className="absolute inset-0 grid place-items-center md:hidden">
                            <Spinner
                                className="size-8 text-white"
                                aria-label="Opening article"
                            />
                        </span>
                    ) : null}
                </span>
            </RoccoLink>

            {/* "Read Article" cursor bubble — eases after the pointer (desktop
                only). The viewport-sized, overflow-hidden wrapper clips it to the
                screen so the bubble (which the rAF can park past an edge) never
                extends the document and adds a scrollbar; the inner node carries
                the eased transform. White circle, dark text for the dark section. */}
            <div
                aria-hidden
                className="pointer-events-none fixed inset-0 z-[110] overflow-hidden max-md:hidden"
            >
                <div ref={bubbleRef} className="absolute top-0 left-0">
                    <div
                        className={cn(
                            'flex size-[8.875rem] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white transition-[scale,opacity] duration-300 ease-out',
                            bubbleVisible
                                ? 'scale-100 opacity-100'
                                : 'scale-50 opacity-0',
                        )}
                    >
                        {opening ? (
                            <Spinner
                                className="size-10 text-[#181818]"
                                aria-label="Opening article"
                            />
                        ) : (
                            <span className="font-sans leading-[1.3] font-medium tracking-[0.02em] text-[#181818] md:text-[0.916rem]">
                                Read Article
                            </span>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
};
